<template>
    <transition appear name="fade-transition">
        <div :class="classList">
            <template v-if="type === 'radio'">
                <label v-if="label" class="edit__boolean__label v-label theme--light" :class="labelClass">
                    <span>{{ label }}</span>
                    <app-tooltip v-if="info || findSlot('info')" :value="info" class="ml-1">
                        <v-icon dense small color="info" style="margin-bottom: 0.1rem">{{
                            getIcon('info', 'fad')
                        }}</v-icon>
                        <template v-if="findSlot('info')" #display>
                            <slot name="info" />
                        </template>
                    </app-tooltip>
                </label>
            </template>

            <div class="edit__boolean__row">
                <edit-selection
                    v-if="type === 'selection'"
                    ref="input"
                    v-model="inputValue"
                    :required="required"
                    :label="label"
                    :items="selectionItems"
                    :rules="validators"
                    hide-details
                    :disabled="disabled"
                    :readonly="readonly"
                    :loading="loading"
                    :clearable="clearable"
                />
                <v-switch
                    v-else-if="type === 'switch'"
                    ref="input"
                    v-model="inputValue"
                    :true-value="true"
                    :false-value="false"
                    :color="color"
                    class="edit__boolean__switch mt-1"
                    dense
                    :rules="validators"
                    hide-details
                    :disabled="disabled"
                    :readonly="readonly"
                    :loading="loading"
                />
                <v-checkbox
                    v-if="type === 'checkbox'"
                    ref="input"
                    v-model="inputValue"
                    :true-value="true"
                    :false-value="false"
                    :color="color"
                    class="edit__boolean__switch"
                    inset
                    dense
                    :rules="validators"
                    hide-details
                    :disabled="disabled"
                    :readonly="readonly"
                    :loading="loading"
                />
                <v-radio-group
                    v-if="type === 'radio'"
                    ref="input"
                    v-model="inputValue"
                    class="edit__boolean__switch"
                    dense
                    :rules="validators"
                    hide-details
                    :disabled="disabled"
                    :readonly="readonly"
                    :loading="loading"
                    :column="column"
                    :row="row"
                >
                    <v-radio :value="true" :color="color">
                        <template #label>
                            <div>
                                <div :class="onDescription ? 'font-weight-bold' : null">
                                    {{ onText ? onText : $t('yes') }}
                                </div>
                                <small v-if="onDescription">{{ onDescription }}</small>
                            </div>
                        </template>
                    </v-radio>
                    <v-radio :value="false" :color="color">
                        <template #label>
                            <div>
                                <div :class="offDescription ? 'font-weight-bold' : null">
                                    {{ offText ? offText : $t('no') }}
                                </div>
                                <small v-if="offDescription">{{ offDescription }}</small>
                            </div>
                        </template>
                    </v-radio>
                </v-radio-group>

                <div v-if="type !== 'radio' && type !== 'selection'">
                    <label
                        v-if="label"
                        class="edit__boolean__label v-label theme--light"
                        :class="labelClass"
                        style="line-height: 1.8"
                    >
                        <span>{{ label }}</span>
                        <app-tooltip v-if="info || findSlot('info')" :value="info" class="ml-1">
                            <v-icon dense small color="info">{{ getIcon('info', 'fad') }}</v-icon>
                            <template v-if="findSlot('info')" #display>
                                <slot name="info" />
                            </template>
                        </app-tooltip>
                    </label>

                    <div v-if="description || findSlot('description')" class="text-caption text--secondary">
                        <p v-if="description" class="mb-0">{{ description }}</p>
                        <slot v-else-if="findSlot('description')" name="description" />
                    </div>
                </div>
            </div>

            <template v-if="type === 'radio'">
                <div v-if="description || findSlot('description')" class="text-caption text--secondary mt-2">
                    <p v-if="description" class="mb-0">{{ description }}</p>
                    <slot v-else-if="findSlot('description')" name="description" />
                </div>
            </template>
            <v-input v-if="!hideDetails" v-model="inputValue" :error-messages="errorMessages" :rules="validators" />
        </div>
    </transition>
</template>

<script>
import Edit from './Edit';

export default {
    name: 'EditBoolean',
    extends: Edit,
    props: {
        defaultValue: {
            type: Boolean,
            default: false
        },
        type: {
            type: String,
            default: 'switch',
            validator: value => ['selection', 'switch', 'checkbox', 'radio'].includes(value)
        },
        color: {
            type: String,
            default: 'success'
        },
        onText: {
            type: String,
            default: null
        },
        onDescription: {
            type: String,
            default: null
        },
        offText: {
            type: String,
            default: null
        },
        offDescription: {
            type: String,
            default: null
        },
        column: {
            type: Boolean,
            default: false
        },
        row: {
            type: Boolean,
            default: true
        },
        labelClass: {
            type: String,
            default: null
        },
        preventSetDefaultValue: {
            type: Boolean,
            default: false
        }
    },
    computed: {
        classList() {
            let classList = ['edit__boolean'];

            if (this.disabled) {
                classList.push('edit__boolean--disabled');
            }

            if (this.type === 'radio') {
                classList.push('edit__boolean--radio');
            }

            if (this.type === 'radio' && (this.onDescription || this.offDescription)) {
                classList.push('edit__boolean--radio-descriptions');
            }

            return classList.join(' ');
        },
        selectionItems() {
            return [
                {
                    text: this.onText ? this.onText : this.$t('yes'),
                    value: true
                },
                {
                    text: this.offText ? this.offText : this.$t('no'),
                    value: false
                }
            ];
        }
    },
    mounted() {
        if (!this.required && this._.isNil(this.value) && !this.preventSetDefaultValue) {
            this.inputValue = this.defaultValue;
        }
    }
};
</script>

<style lang="scss">
.edit__boolean {
    position: relative;

    &--form {
        margin-bottom: 1.5rem;
    }

    &__row {
        display: flex;
        flex-direction: row;
        align-items: flex-start;
    }

    &__switch {
        margin-top: 0;
        padding-top: 0.2rem;
        padding-left: 0.2rem;
    }

    &__description {
        font-size: 0.9rem;
        cursor: pointer;
    }

    &--radio {
        .edit__boolean__row {
            display: block;
        }

        .edit__boolean__label {
            position: static;
            margin-bottom: 0.4rem;
            display: block;
            top: auto;
            left: auto;
            transform: none;
        }
    }

    &--disabled {
        .edit__boolean {
            &__description {
                cursor: default;
            }
        }
        .edit__boolean__label {
            opacity: 0.7;
        }
    }

    .edit__selection {
        min-width: 6rem;
    }
}

.v-card__text .edit__boolean__label {
    font-size: 0.875rem;
}
</style>
